import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import { User, type IOrg, type IUser } from "@/types";
import { useSessionStore } from "@/stores/session-store";

const BASE = `${import.meta.env.VITE_API_URL}/users`;

// READ — current user profile
export function useUserProfile(enabled: Ref<boolean> = computed(() => true)) {
    const { data: profile, isFetching: profileLoading, error: profileError, refetch: refetchProfile } = useQuery({
        queryKey: ["user-profile"],
        enabled,
        queryFn: () => fetchWrapper.get<IUser>(`${BASE}/profile`),
    });

    return { profile, profileLoading, profileError: profileError as Ref<ApiError | null>, refetchProfile };
}

// READ — user's org memberships
export function useUserMemberships(enabled: Ref<boolean> = computed(() => true)) {
    const { data, isFetching: membershipsLoading, error: membershipsError } = useQuery({
        queryKey: ["user-memberships"],
        enabled,
        queryFn: () => fetchWrapper.get<IOrg[]>(`${BASE}/memberships`),
    });
    const memberships = computed(() => data.value ?? []);

    return { memberships, membershipsLoading, membershipsError: membershipsError as Ref<ApiError | null> };
}

// WRITE — update user profile
export function useUpdateUserProfile() {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, IUser>({
        mutationFn: (user: IUser) => fetchWrapper.put(`${BASE}/${user.id}`, user),
        onSuccess: (_data, user) => {
            queryClient.invalidateQueries({ queryKey: ["user-profile"] });
            useSessionStore().session.user = new User(user);
        },
    });

    return {
        updateUserProfile: mutate,
        updateUserProfileAsync: mutateAsync,
        updateUserProfilePending: isPending,
        updateUserProfileError: error as Ref<ApiError | null>,
        updateUserProfileReset: reset,
    };
}

// WRITE — update user theme (partial update, nav toggle)
export function useUpdateUserTheme() {
    const queryClient = useQueryClient();

    const { mutate } = useMutation<void, ApiError, string>({
        mutationFn: (theme: string) => {
            const userId = useSessionStore().session.user.id;
            return fetchWrapper.put(`${BASE}/${userId}`, { settings: { theme } });
        },
        onSuccess: (_data, theme) => {
            useSessionStore().session.user.settings.theme = theme;
            queryClient.invalidateQueries({ queryKey: ["user-profile"] });
        },
    });

    return { updateUserTheme: mutate };
}
